// components/views/AnalyticsView.jsx
import { useState, useMemo } from 'react';
import { Callout, LoadRow, Icons } from '../UI';
import { STAGES, dgsCheck, fmtMoney, fmtDate, daysAgo } from '../../utils/constants';

const PHASE_MAP = { 0:0,1:0,2:0,3:0,4:0, 5:1,6:1,7:1,8:1, 9:2,10:2,11:2, 12:3,13:3, 14:4 };
const PHASE_INFO = [
  { label: 'Pre-Solicitation', color: '#C4992A' },
  { label: 'Solicitation',     color: '#60A5FA' },
  { label: 'Award',            color: '#F97316' },
  { label: 'Execution',        color: '#34D399' },
  { label: 'Handoff',          color: '#A78BFA' },
];

function StatCard({ label, value, sub, color, alert }) {
  return (
    <div style={{
      background: 'var(--white)', border: '1.5px solid ' + (alert ? 'var(--red-200)' : 'var(--slate-200)'),
      borderRadius: 'var(--r-lg)', padding: '16px 20px',
      borderTop: '3px solid ' + (color || 'var(--navy-700)'),
    }}>
      <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--slate-400)', marginBottom: 8 }}>{label}</div>
      <div style={{ fontSize: 28, fontWeight: 800, color: alert ? 'var(--red-600)' : (color || 'var(--navy-800)') }}>{value}</div>
      {sub && <div style={{ fontSize: 12, color: 'var(--slate-400)', marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

function BarRow({ label, count, maxCount, spend, maxSpend, color, showSpend }) {
  const pct = showSpend ? Math.round(spend / maxSpend * 100) : Math.round(count / maxCount * 100);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
      <div style={{ fontSize: 12, color: 'var(--slate-500)', width: 110, flexShrink: 0 }}>{label}</div>
      <div style={{ flex: 1, height: 10, background: 'var(--slate-100)', borderRadius: 99, overflow: 'hidden' }}>
        <div style={{ width: pct + '%', height: '100%', background: color, borderRadius: 99, transition: 'width 0.4s ease' }} />
      </div>
      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--slate-600)', width: 70, textAlign: 'right', flexShrink: 0 }}>
        {showSpend ? fmtMoney(spend) : count}
      </div>
    </div>
  );
}

function ReportRow({ r, onOpen }) {
  const flag = r.dgsRequired || (r.stageNum === 10 && !r.unionNotified) || (r.daysInStage || 0) > 30;
  const warn = (r.daysInStage || 0) > 14 && (r.daysInStage || 0) <= 30;
  const dc = (r.daysInStage || 0) > 30 ? 'days-alert' : (r.daysInStage || 0) > 14 ? 'days-warn' : 'days-ok';
  return (
    <div className={`rcr ${flag ? 'flag' : warn ? 'warn' : ''}`} onClick={onOpen} style={{ marginBottom: 8 }}>
      <div className="rcr-header">
        <div className="rcr-title">{r.title}</div>
        {r.agreementNum && <span style={{ fontSize: 10, color: 'rgba(255,255,255,.4)', fontFamily: 'var(--font-mono)' }}>{r.agreementNum}</span>}
        <span className={`days-badge ${dc}`}>{r.daysInStage !== null ? r.daysInStage + 'd' : '?'}</span>
        <span className="badge badge-gold" style={{ fontSize: 9, marginLeft: 4 }}>{STAGES[r.stageNum]?.short}</span>
      </div>
      <div className="rcr-grid">
        <div className="rcr-field"><div className="rcr-lbl">Stage</div><div className="rcr-val">{r.stage}</div></div>
        <div className="rcr-field"><div className="rcr-lbl">Time in Stage</div><div className="rcr-val" style={{ color: (r.daysInStage||0)>30?'#FCA5A5':(r.daysInStage||0)>14?'#FCD34D':'inherit' }}>{r.daysInStage !== null ? r.daysInStage + 'd' : '—'}</div></div>
        <div className="rcr-field"><div className="rcr-lbl">Budget</div><div className="rcr-val">{r.budget ? fmtMoney(r.budget) : '—'}</div></div>
        <div className="rcr-field"><div className="rcr-lbl">Contractor</div><div className="rcr-val">{r.contractorName || <span style={{ opacity: .5 }}>Not awarded</span>}</div></div>
        {r.nextStage && <div className="rcr-next"><strong>Next:</strong> {r.nextStage}</div>}
        {(flag || warn) && (
          <div className="rcr-flags">
            {r.dgsRequired && (r.dgsOtaRequired && r.dgsOlsRequired
              ? <><span className="flag-chip flag-red">DGS/OTA</span><span className="flag-chip flag-orange" style={{ marginLeft: 4 }}>DGS/OLS</span></>
              : <span className="flag-chip flag-orange">DGS/OLS</span>)}
            {r.stageNum === 10 && !r.unionNotified && <span className="flag-chip flag-red">Union Notice Pending</span>}
            {(r.daysInStage||0) > 30 && <span className="flag-chip flag-red">{r.daysInStage}d Overdue</span>}
            {(r.daysInStage||0) > 14 && (r.daysInStage||0) <= 30 && <span className="flag-chip flag-yellow">{r.daysInStage}d — Monitor</span>}
          </div>
        )}
      </div>
    </div>
  );
}

export default function AnalyticsView({ contracts, paal, profile, openContract, apiKey, callAI, todayStr }) {
  const [showReport, setShowReport] = useState(false);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportResult, setReportResult] = useState('');
  const [copied, setCopied] = useState(false);
  const [chartMode, setChartMode] = useState('count');

  const active = useMemo(() => contracts.filter(c => !c.archived), [contracts]);
  const archived = useMemo(() => contracts.filter(c => c.archived), [contracts]);

  const an = useMemo(() => {
    const stageCounts = Array(STAGES.length).fill(0);
    active.forEach(c => { if (c.stage >= 0 && c.stage < STAGES.length) stageCounts[c.stage]++; });
    const totalBudget = active.reduce((s, c) => s + (parseFloat(c.budget) || 0), 0);
    const avgBudget = active.length ? totalBudget / active.length : 0;
    const dgsNeeded = active.filter(c => !c.exemption?.active && dgsCheck(c, paal).required).length;
    const dvbePending = active.filter(c => c.stage === 7 && (!c.dvbe?.solMethod || !c.dvbe?.sbGoal)).length;
    const activeCount = active.filter(c => c.stage >= 1 && c.stage <= 13).length;
    const stalledCount = active.filter(c => {
      const last = [...(c.stageHistory || [])].reverse()[0];
      return last && daysAgo(last.date) > 14;
    }).length;
    const groups = PHASE_INFO.map((phase, pi) => {
      const pc = active.filter(c => PHASE_MAP[c.stage] === pi);
      return { ...phase, count: pc.length, spend: pc.reduce((s, c) => s + (parseFloat(c.budget) || 0), 0) };
    });
    const maxCount = Math.max(...groups.map(g => g.count), 1);
    const maxSpend = Math.max(...groups.map(g => g.spend), 1);
    const byIT = { it: active.filter(c => c.isIT === 'yes').length, nonIT: active.filter(c => c.isIT !== 'yes').length };
    const methodMap = {};
    active.forEach(c => { const m = c.procMethod || 'Unspecified'; methodMap[m] = (methodMap[m] || 0) + 1; });
    const methods = Object.entries(methodMap).sort((a, b) => b[1] - a[1]).slice(0, 6);
    const maxMethod = Math.max(...methods.map(m => m[1]), 1);
    return { stageCounts, totalBudget, avgBudget, dgsNeeded, dvbePending, activeCount, stalledCount, groups, maxCount, maxSpend, byIT, methods, maxMethod, total: active.length };
  }, [active, paal]);

  const reportRows = useMemo(() => active.map(c => {
    const last = [...(c.stageHistory || [])].reverse()[0];
    const stageDate = last?.date || c.createdAt;
    const dgs = dgsCheck(c, paal);
    return {
      id: c.id, title: c.title || 'Untitled', agreementNum: c.s215?.agreementNum || '',
      stage: STAGES[c.stage]?.label || '', stageNum: c.stage, stageDate,
      daysInStage: daysAgo(stageDate), budget: c.budget, procMethod: c.procMethod, isIT: c.isIT,
      contractorName: c.s215?.contractorName || '',
      dgsRequired: !c.exemption?.active && dgs.required,
      dgsOtaRequired: !c.exemption?.active && dgs.otaRequired,
      dgsOlsRequired: !c.exemption?.active && dgs.olsRequired,
      unionNotified: c.s215?.unionNotified, nextStage: STAGES[c.stage + 1]?.label || null, exemption: c.exemption,
    };
  }).sort((a, b) => (b.daysInStage || 0) - (a.daysInStage || 0)), [active, paal]);

  const flagged = reportRows.filter(r => r.dgsRequired || (r.stageNum === 10 && !r.unionNotified) || (r.daysInStage || 0) > 30);

  function buildPlainReport() {
    const lines = ['WEEKLY CONTRACT STATUS REPORT', 'Agency: ' + (profile.agencyName || 'Energy Safety'), 'Prepared by: ' + (profile.contactName || '[Name]'), 'Date: ' + todayStr, '='.repeat(60), 'SUMMARY: ' + an.total + ' active | ' + an.dgsNeeded + ' DGS pending', ''];
    reportRows.forEach((r, i) => {
      lines.push((i+1) + '. ' + r.title.toUpperCase());
      if (r.agreementNum) lines.push('   Agreement: ' + r.agreementNum);
      lines.push('   Stage: ' + r.stage + ' | Time: ' + (r.daysInStage !== null ? r.daysInStage + 'd' : '—') + ' | Budget: ' + fmtMoney(r.budget));
      if (r.contractorName) lines.push('   Contractor: ' + r.contractorName);
      lines.push('   Next: ' + (r.nextStage || 'Contract closed'));
      if (r.dgsRequired) lines.push('   !! ' + (r.dgsOtaRequired && r.dgsOlsRequired ? 'DGS/OTA + DGS/OLS REQUIRED' : 'DGS/OLS REQUIRED'));
      if (r.stageNum === 10 && !r.unionNotified) lines.push('   !! UNION NOTIFICATION PENDING');
      lines.push('');
    });
    return lines.join('\n');
  }

  async function runAIReport() {
    setReportLoading(true);
    try {
      const summary = reportRows.slice(0,15).map((r,i) => (i+1)+'. '+r.title+' | Stage: '+r.stage+' | '+(r.daysInStage !== null ? r.daysInStage+'d' : '—')+' | Budget: '+fmtMoney(r.budget)+(r.dgsRequired ? ' | DGS REQUIRED' : '')).join('\n');
      const sys = 'You are a CA state contracting supervisor preparing a weekly briefing for management at Energy Safety. Write a concise, professional briefing memo covering portfolio status, key risks, required actions, and recommendations. 3-5 paragraphs. SCM-aligned.';
      const msg = 'Weekly briefing for ' + (profile.contactName || 'the contract team') + ' at ' + (profile.agencyName || 'Energy Safety') + '.\nDate: ' + todayStr + '\nPortfolio: ' + an.total + ' contracts | $' + Math.round(an.totalBudget/1000) + 'K total | ' + an.activeCount + ' active | ' + an.dgsNeeded + ' requiring DGS\n\nContracts:\n' + summary;
      const text = await callAI(apiKey, { system: sys, messages: [{ role: 'user', content: msg }], max_tokens: 600 });
      setReportResult(text);
    } catch (e) { setReportResult('Error: ' + e.message); }
    finally { setReportLoading(false); }
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 800, color: 'var(--navy-900)', marginBottom: 4 }}>Analytics</h1>
          <p style={{ fontSize: 13.5, color: 'var(--slate-500)' }}>{an.total} active · {archived.length} archived · {fmtMoney(an.totalBudget)} portfolio</p>
        </div>
        <button className="btn btn-ghost" disabled={!active.length} onClick={() => { setShowReport(true); if (!reportResult) runAIReport(); }}>
          <Icons.sparkle /> Weekly Report
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 14 }}>
        <StatCard label="Active Contracts" value={an.total} sub={an.activeCount + ' in flight'} color="var(--navy-700)" />
        <StatCard label="Portfolio Value" value={fmtMoney(an.totalBudget)} sub={'Avg ' + fmtMoney(an.avgBudget)} color="var(--gold-500)" />
        <StatCard label="DGS Required" value={an.dgsNeeded} sub="above PAAL threshold" color={an.dgsNeeded > 0 ? 'var(--red-500)' : 'var(--green-500)'} alert={an.dgsNeeded > 0} />
        <StatCard label="Archived" value={archived.length} sub="completed contracts" color="var(--slate-400)" />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 24 }}>
        <StatCard label="IT Contracts" value={an.byIT.it} sub={an.byIT.nonIT + ' non-IT'} color="var(--blue-500)" />
        <StatCard label="SB/DVBE Pending" value={an.dvbePending} sub="at stage 8" color={an.dvbePending > 0 ? 'var(--orange-500)' : 'var(--green-500)'} alert={an.dvbePending > 0} />
        <StatCard label="Stalled >14 Days" value={an.stalledCount} sub="need attention" color={an.stalledCount > 0 ? 'var(--red-500)' : 'var(--green-500)'} alert={an.stalledCount > 0} />
        <StatCard label="Flagged" value={flagged.length} sub="need action now" color={flagged.length > 0 ? 'var(--red-500)' : 'var(--green-500)'} alert={flagged.length > 0} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h3 className="card-title" style={{ margin: 0 }}>Contracts by Phase</h3>
            <div style={{ display: 'flex', gap: 4 }}>
              {['count', 'spend'].map(m => (
                <button key={m} onClick={() => setChartMode(m)} style={{ padding: '3px 10px', borderRadius: 99, fontSize: 11, fontWeight: 600, background: chartMode === m ? 'var(--navy-800)' : 'transparent', color: chartMode === m ? 'var(--white)' : 'var(--slate-400)', border: '1px solid ' + (chartMode === m ? 'var(--navy-800)' : 'var(--slate-200)'), cursor: 'pointer' }}>{m === 'count' ? '# Count' : '$ Value'}</button>
              ))}
            </div>
          </div>
          {an.groups.map(g => <BarRow key={g.label} label={g.label} count={g.count} maxCount={an.maxCount} spend={g.spend} maxSpend={an.maxSpend} color={g.color} showSpend={chartMode === 'spend'} />)}
        </div>
        <div className="card">
          <h3 className="card-title">Procurement Methods</h3>
          {an.methods.length === 0 ? <div style={{ fontSize: 13, color: 'var(--slate-400)', fontStyle: 'italic' }}>No contracts yet</div>
            : an.methods.map(([method, count]) => <BarRow key={method} label={method} count={count} maxCount={an.maxMethod} spend={0} maxSpend={1} color="var(--blue-400)" showSpend={false} />)}
        </div>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <h3 className="card-title">Stage Breakdown</h3>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {STAGES.map((s, i) => {
            const count = an.stageCounts[i];
            return (
              <div key={i} style={{ flex: '1 1 72px', minWidth: 64, padding: '10px 8px', background: count > 0 ? 'var(--gold-50)' : 'var(--slate-50)', border: '1.5px solid ' + (count > 0 ? 'var(--gold-300)' : 'var(--slate-200)'), borderRadius: 'var(--r-md)', textAlign: 'center' }}>
                <div style={{ fontSize: 20, fontWeight: 700, color: count > 0 ? 'var(--gold-600)' : 'var(--slate-300)' }}>{count}</div>
                <div style={{ fontSize: 9, color: 'var(--slate-500)', marginTop: 3, lineHeight: 1.3 }}>{s.short}</div>
              </div>
            );
          })}
        </div>
      </div>

      {flagged.length > 0 && (
        <div className="card" style={{ borderLeft: '3px solid var(--red-400)', marginBottom: 16 }}>
          <h3 className="card-title" style={{ color: 'var(--red-600)' }}>⚠ {flagged.length} Contract{flagged.length !== 1 ? 's' : ''} Need Attention</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {flagged.map(r => (
              <div key={r.id} onClick={() => openContract(r.id)} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', background: 'var(--red-50)', borderRadius: 'var(--r-md)', cursor: 'pointer', border: '1px solid var(--red-100)' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--navy-800)' }}>{r.title}</div>
                  <div style={{ fontSize: 12, color: 'var(--slate-500)' }}>{r.stage} · {r.daysInStage}d in stage</div>
                </div>
                <div style={{ display: 'flex', gap: 4 }}>
                  {r.dgsRequired && <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 99, background: 'var(--orange-100)', color: 'var(--orange-700)' }}>DGS</span>}
                  {(r.daysInStage||0) > 30 && <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 99, background: 'var(--red-100)', color: 'var(--red-700)' }}>Stalled</span>}
                  {r.stageNum === 10 && !r.unionNotified && <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 99, background: 'var(--red-100)', color: 'var(--red-700)' }}>Union</span>}
                </div>
                <span style={{ fontSize: 12, color: 'var(--slate-400)' }}>→</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {showReport && (
        <div className="report-overlay" onClick={e => { if (e.target === e.currentTarget) setShowReport(false); }}>
          <div className="report-container">
            <div className="report-header">
              <div>
                <div className="report-title">Weekly Contract Status Report</div>
                <div className="report-meta">{todayStr} · {profile.contactName || profile.agencyName}</div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-ghost btn-sm" style={{ color: 'var(--gold-300)', borderColor: 'rgba(255,255,255,.15)' }} onClick={() => { navigator.clipboard.writeText(buildPlainReport()); setCopied(true); setTimeout(() => setCopied(false), 2000); }}>{copied ? '✓ Copied' : 'Copy Text'}</button>
                {!reportLoading && <button className="btn btn-ghost btn-sm" style={{ color: 'var(--gold-300)', borderColor: 'rgba(255,255,255,.15)' }} onClick={runAIReport}><Icons.sparkle /> {reportResult ? 'Refresh' : 'Ask Penny'}</button>}
                <button className="btn btn-ghost btn-sm" style={{ color: 'var(--navy-300)', borderColor: 'rgba(255,255,255,.1)' }} onClick={() => { setShowReport(false); setReportResult(''); }}>Close</button>
              </div>
            </div>
            <div className="report-body">
              {(reportLoading || reportResult) && (
                <div style={{ background: 'rgba(96,165,250,.1)', border: '1px solid rgba(96,165,250,.2)', borderRadius: 'var(--r-md)', padding: 16, marginBottom: 20 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em', color: 'var(--navy-300)', marginBottom: 10 }}>Penny Briefing Memo</div>
                  {reportLoading ? <LoadRow msg="Generating briefing memo…" /> : <div style={{ fontSize: 13, lineHeight: 1.8, color: 'var(--navy-100)', whiteSpace: 'pre-wrap' }}>{reportResult}</div>}
                </div>
              )}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginBottom: 20 }}>
                {[['Total', an.total], ['Need Action', flagged.length], ['Active', an.activeCount], ['DGS Req', an.dgsNeeded]].map(([label, val]) => (
                  <div key={label} style={{ textAlign: 'center', padding: '10px 8px', background: 'rgba(255,255,255,.04)', borderRadius: 'var(--r-md)' }}>
                    <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--gold-300)' }}>{val}</div>
                    <div style={{ fontSize: 10, color: 'var(--navy-400)', textTransform: 'uppercase', letterSpacing: '.06em', marginTop: 3 }}>{label}</div>
                  </div>
                ))}
              </div>
              {flagged.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em', color: '#FCA5A5', marginBottom: 8 }}>Needs Attention ({flagged.length})</div>
                  {flagged.map(r => <ReportRow key={r.id} r={r} onOpen={() => { setShowReport(false); openContract(r.id); }} />)}
                </div>
              )}
              <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em', color: 'var(--navy-400)', marginBottom: 8 }}>All Contracts — Sorted by Days in Stage</div>
              {reportRows.length === 0 ? <div style={{ color: 'var(--navy-400)', textAlign: 'center', padding: 24 }}>No contracts yet</div>
                : reportRows.map(r => <ReportRow key={r.id} r={r} onOpen={() => { setShowReport(false); openContract(r.id); }} />)}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
